import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

declare var webkitSpeechRecognition: any;
declare var MediaRecorder: any;

@Injectable({
  providedIn: 'root',
})
export class VoiceRecognitionService {
  recognition: any = typeof webkitSpeechRecognition !== 'undefined' ? new webkitSpeechRecognition() : null;
  public isStoppedSpeechRecog = false;
  /** Committed (finalised) transcript. Consumers may seed this before start() to append to existing text. */
  public text = '';
  /** Live interim transcript for the phrase currently being spoken (not yet finalised). */
  public tempWords = '';
  public transcribed: boolean = false;

  /** Emits a human-readable message when recognition can't run (unsupported / mic blocked). */
  readonly error$ = new Subject<string>();

  constructor() {}

  /** True when the Web Speech API is available in this browser (Chrome/Edge). */
  get isSupported(): boolean {
    return typeof (window as any).webkitSpeechRecognition !== 'undefined' || typeof (window as any).SpeechRecognition !== 'undefined';
  }

  init() {
    this.transcribed = false;
    this.text = '';
    this.tempWords = '';

    if (!this.isSupported) {
      return;
    }

    const Recognition = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
    this.recognition = new Recognition();
    // continuous keeps the mic open across pauses; interimResults gives live, word-by-word feedback.
    this.recognition.continuous = true;
    this.recognition.interimResults = true;
    this.recognition.lang = 'en-US';

    this.recognition.addEventListener('result', e => {
      let interim = '';
      let final = '';
      // Newer browsers expose resultIndex/isFinal; older mocks don't, so fall back to interim.
      const startIdx = typeof e.resultIndex === 'number' ? e.resultIndex : 0;
      for (let i = startIdx; i < e.results.length; i++) {
        const result = e.results[i];
        const transcript = result?.[0]?.transcript ?? '';
        if (result?.isFinal) {
          final += transcript + ' ';
        } else {
          interim += transcript;
        }
      }
      // Finalised words are appended to the committed text (preserving any seed); interim stays live.
      if (final) {
        this.text = `${this.text} ${final}`.replace(/\s+/g, ' ').trimStart();
      }
      this.tempWords = interim;
      this.transcribed = true;
    });

    this.recognition.addEventListener('error', (e: any) => {
      const code = e?.error;
      // no-speech / aborted are benign — the end handler restarts the session.
      if (code === 'no-speech' || code === 'aborted') {
        return;
      }
      this.isStoppedSpeechRecog = true;
      if (code === 'not-allowed' || code === 'service-not-allowed') {
        this.error$.next('Microphone access is blocked. Allow mic permission in your browser and try again.');
      } else if (code === 'audio-capture') {
        this.error$.next('No microphone was found. Connect a mic and try again.');
      } else {
        this.error$.next('Voice typing stopped unexpectedly. Please try again.');
      }
    });
  }

  start() {
    if (!this.isSupported) {
      this.error$.next('Voice typing is not supported in this browser. Try Google Chrome.');
      return;
    }
    this.init();
    this.startmeter();
    this.isStoppedSpeechRecog = false;
    try {
      this.recognition.start();
    } catch {
      // start() throws if called while already running — safe to ignore.
    }
    this.recognition.addEventListener('end', () => {
      if (this.isStoppedSpeechRecog) {
        this.recognition.stop();
      } else {
        this.wordConcat();
        try {
          this.recognition.start();
        } catch {
          /* already started */
        }
      }
    });
  }

  public volumeMeterValue: any = `width:0%`;
  public stream: any;
  public chunks: any = [];
  public analyserNode: any;
  public mediaRecorder: any;
  public audioblob: any;
  async startmeter() {
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    } catch {
      this.error$.next('Microphone access is blocked. Allow mic permission in your browser and try again.');
      return;
    }
    let mediaRecorder = new MediaRecorder(this.stream);
    mediaRecorder.ondataavailable = data => {
      this.chunks.push(data.data);
    };
    mediaRecorder.onstop = () => {
      this.audioblob = new Blob(this.chunks, { type: 'audio/mp3' });
    };
    mediaRecorder.start();

    const audioContext = new AudioContext();
    const mediaStreamAudioSourceNode = audioContext.createMediaStreamSource(this.stream);
    this.analyserNode = audioContext.createAnalyser();
    mediaStreamAudioSourceNode.connect(this.analyserNode);

    const pcmData = new Float32Array(this.analyserNode.fftSize);
    const onFrame = () => {
      if (this.analyserNode) {
        this.analyserNode.getFloatTimeDomainData(pcmData);
        let sumSquares = 0.0;
        for (const amplitude of pcmData) {
          sumSquares += amplitude * amplitude;
        }
        this.volumeMeterValue = `width:${Math.sqrt(sumSquares / pcmData.length) * 300}%`;
        window.requestAnimationFrame(onFrame);
      }
    };
    window.requestAnimationFrame(onFrame);
  }
  stopmeter() {
    if (this.stream) {
      this.stream.getTracks().forEach(function (track) {
        track.stop();
      });
    }
    this.analyserNode = null;
  }

  stop() {
    this.isStoppedSpeechRecog = true;
    this.wordConcat();
    this.stopmeter();
    try {
      this.recognition?.stop();
    } catch {
      // stop() throws if not running — safe to ignore.
    }
    this.transcribed = false;
  }

  wordConcat() {
    if (this.tempWords) this.text = this.text + ' ' + this.tempWords + ' ';

    this.tempWords = '';
  }
}
